namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionPortfolioValue {
    entityId?: number;
    investmentId?: number;
    resourceId?: number;
  }

  export interface AttributionGroupByValue {
    id?: number;
    label?: string;
  }

  /**
   * Builds the Custom CompositeSelectionSetData that IntervalStatsDataSetLoader expects,
   * from the Attribution Report's existing "portfolio", "groupBy", and "otherOptions"
   * parameters.
   *
   * IntervalStatsDataSetLoader.Composite requires exactly one PropertyValue grouping
   * declaration and CreateOtherGroups = true for attribution to compute at all (both
   * enforced server-side, InvData.CoreDataSets/IntervalStats/IntervalStatsDataSetLoader.Composite.cs)
   * -- there is no such thing as an "ungrouped" attribution composite. Shared by both
   * AttributionReport.js data sources: the attribution table (dataSourceForAttributionTable)
   * renders one row per composite node under this grouping, so groupBy directly determines
   * the row breakdown, not just the "All" aggregate. The KPI tiles (dataSourceForKpiTiles)
   * only ever read the top-level "All" row, so the choice of grouping property doesn't
   * affect their values -- the "All" aggregate is the same regardless of how the breakdown
   * beneath it is organized -- but groupBy is still required here since the server-side
   * grouping requirement applies regardless of which consumer is calling.
   *
   * Returns null instead of a composite ref (rather than one with no owner scoping) whenever
   * portfolio can't be mapped to ownerEntityIds/investmentIds -- this covers both a missing
   * portfolio and a portfolio resolved only to a bare resourceId (CompositeSelectionSetData has
   * no resource-based ownership scoping, and entityOrInvestment values dropped in via Solovis ref
   * drag/drop can resolve to resourceId only, per EntityOrInvestmentParameterControl). Building an
   * unscoped, environment-wide composite here would be silently wrong rather than failing loudly.
   * Both callers skip loading on a missing groupBy/time period/policyBenchmarks, and additionally
   * treat a null return from here the same way.
   */
  export function buildAttributionCompositeSelectionSetRef(
    portfolio: AttributionPortfolioValue,
    groupBy: AttributionGroupByValue,
    otherOptions?: AttributionOtherOptions
  ): { custom: Solovis.Reports.Parameters.Composites.CompositeSelectionSetData } | null {

    if (!portfolio || !(portfolio.entityId || portfolio.investmentId)) {
      return null;
    }

    const configurations = otherOptions && otherOptions.configurations;

    const custom: Solovis.Reports.Parameters.Composites.CompositeSelectionSetData = {
      removeEmptyComponents: resolveRemoveEmptyComponents(configurations),
      createOtherGroups: true,
      groupingDeclarations: [
        { declarationType: "PropertyValue", propertyDefinitionId: groupBy.id, label: groupBy.label }
      ]
    };

    if (portfolio.entityId) {
      custom.ownerEntityIds = [portfolio.entityId];
    }
    else {
      custom.investmentIds = [portfolio.investmentId];
    }

    if (configurations && configurations.allLabel) {
      custom.allLabel = configurations.allLabel;
    }

    return { custom: custom };
  }
}
